import { getAccessToken, logout } from './auth';

export interface ErrorPattern {
  id:          number;
  description: string;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api/error-patterns`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(
      body.reason ?? `Server-Fehler ${resp.status}`,
      resp.status,
      body.result,
      body.fields,
    );
  }

  return body;
}

export async function list(): Promise<ErrorPattern[]> {
  const body = await request<ErrorPattern>(BASE, { method: 'GET' });
  return body.items ?? [];
}

export async function create(description: string): Promise<ErrorPattern> {
  const body = await request<ErrorPattern>(BASE, {
    method: 'POST',
    body:   JSON.stringify({ description }),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function update(id: number, description: string): Promise<ErrorPattern> {
  const body = await request<ErrorPattern>(`${BASE}/${id}`, {
    method: 'PATCH',
    body:   JSON.stringify({ description }),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function disable(id: number): Promise<void> {
  await request<ErrorPattern>(`${BASE}/${id}`, { method: 'DELETE' });
}
